/* eslint-disable react/forbid-prop-types, react/require-default-props */
import React, { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";

import { CHART_TYPES, ACCENTS, accentById } from "./widgetTypes";
import { StatTile, TrendChart, BarChartW, DonutW, TableW, GaugeW, DotMatrixW, HeatmapW, CompareW } from "./Charts";
import { COMBINE_OPS, resolveWidgetData, formatMetricValue } from "./useDashboardMetrics";
import { IcClose, IcCheck } from "./icons";

/* Compact metric picker: a small popover with a search bar — never the
   native browser select (which opens a giant OS list). */
function MetricSelect({ value, options, onChange, ariaLabel }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("pointerdown", onDown);
    return () => document.removeEventListener("pointerdown", onDown);
  }, [open]);
  const current = options.find((m) => m.id === value);
  const list = options.filter((m) => !q || m.title.toLowerCase().includes(q.toLowerCase()));
  return (
    <div className="db-mselect" ref={ref}>
      <button
        type="button"
        className="db-mselect-btn"
        aria-label={ariaLabel}
        aria-expanded={open}
        onClick={() => {
          setOpen((v) => !v);
          setQ("");
        }}
      >
        <span className="t">{current?.title || value || "Pick a metric"}</span>
        <svg className="chev" width="10" height="6" viewBox="0 0 10 6" aria-hidden="true">
          <path d="M1 1l4 4 4-4" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>
      {open && (
        <div className="db-mselect-pop">
          <input
            className="db-mselect-search"
            placeholder="Search metrics"
            value={q}
            // eslint-disable-next-line jsx-a11y/no-autofocus
            autoFocus
            onChange={(e) => setQ(e.target.value)}
          />
          <div className="db-mselect-list">
            {list.map((m) => (
              <button
                key={m.id}
                type="button"
                className={m.id === value ? "on" : ""}
                onClick={() => {
                  onChange(m.id);
                  setOpen(false);
                }}
              >
                <span>{m.title}</span>
                {m.id === value && <IcCheck size={12} />}
              </button>
            ))}
            {!list.length && <div className="db-mselect-empty">No metrics match that search.</div>}
          </div>
        </div>
      )}
    </div>
  );
}
MetricSelect.propTypes = {
  value: PropTypes.string,
  options: PropTypes.array,
  onChange: PropTypes.func.isRequired,
  ariaLabel: PropTypes.string,
};

/* Live preview of the card exactly as it will land on the board. */
function EditPreview({ draft, api }) {
  const computed = !!(draft.options?.combine || draft.options?.avgDivisor);
  const { metric, series, summary } = api ? resolveWidgetData(api, draft) : { metric: null, series: [], summary: {} };
  const accent = accentById(draft.options?.accent);
  const accentVars = accent.c
    ? { "--db-brand": accent.c, "--db-brand-2": accent.c2, "--db-brand-soft": accent.soft }
    : undefined;
  const tinted = draft.options?.fill === "tint";

  if (!metric) {
    return (
      <div className="db-edit-preview">
        <div className="lab">Preview</div>
        <div className="db-edit-noprev">Pick a metric to see the card.</div>
      </div>
    );
  }

  const t = draft.chartType || "stat";
  let body = null;
  if (t === "stat") body = <StatTile metric={metric} summary={summary} series={series} graphPos={draft.options?.graph || "bottom"} />;
  else if (t === "line") body = <TrendChart metric={metric} series={series} area={false} />;
  else if (t === "area") body = <TrendChart metric={metric} series={series} area />;
  else if (t === "bar") body = <BarChartW metric={metric} series={series} />;
  else if (t === "donut") body = <DonutW metric={metric} summary={summary} />;
  else if (t === "gauge") body = <GaugeW metric={metric} summary={summary} />;
  else if (t === "dots") body = <DotMatrixW metric={metric} series={series} />;
  else if (t === "heatmap") body = <HeatmapW metric={metric} series={series} />;
  else if (t === "table") body = <TableW metric={metric} series={series} />;
  else if (t === "compare") {
    const extraIds = (draft.options?.metrics || []).filter((id) => id !== draft.metric).slice(0, 3);
    const items = [
      { metric, series, summary, color: accent.c || "var(--db-brand)" },
      ...extraIds.map((id, i) => ({
        metric: api?.getMetric ? api.getMetric(id) : null,
        series: api?.getSeries ? api.getSeries(id, draft.options?.granularity) : [],
        summary: api?.getSummary ? api.getSummary(id) : {},
        color: ACCENTS.filter((a) => a.c)[i % 5]?.c,
      })),
    ];
    body = <CompareW items={items} />;
  }

  return (
    <div className="db-edit-preview">
      <div className="lab">Preview · live</div>
      <div
        className="db-edit-card"
        style={tinted ? { background: `linear-gradient(160deg, ${accent.soft || "rgba(27, 89, 142, 0.10)"}, #fff 70%)` } : undefined}
      >
        <div className="t">{draft.title || metric.title}</div>
        <div className="b" style={accentVars}>{body}</div>
      </div>
      <div className="cap">
        {computed ? `= ${formatMetricValue(metric, summary.total)} · ${metric.title}` : metric.title}
        {summary.periodLabel ? ` · ${summary.periodLabel}` : ""}
      </div>
    </div>
  );
}
EditPreview.propTypes = { draft: PropTypes.object, api: PropTypes.object };

/** Edit a widget in a centered glass modal: title, metric, chart type, grouping. */
export default function WidgetEditDrawer({ open, widget, metrics, api, onSave, onClose }) {
  const [draft, setDraft] = useState(widget || {});

  useEffect(() => {
    if (open && widget) setDraft({ ...widget });
  }, [open, widget]);

  if (!open || !widget) return null;
  const set = (k, v) => setDraft((d) => ({ ...d, [k]: v }));

  const granularity = draft?.options?.granularity || "day";
  const setGran = (g) => setDraft((d) => ({ ...d, options: { ...(d.options || {}), granularity: g } }));
  const accent = draft?.options?.accent || "brand";
  const setAccent = (a) => setDraft((d) => ({ ...d, options: { ...(d.options || {}), accent: a } }));
  const graph = draft?.options?.graph || "bottom";
  const setGraph = (g) => setDraft((d) => ({ ...d, options: { ...(d.options || {}), graph: g } }));
  const fill = draft?.options?.fill || "white";
  const setFill = (f) => setDraft((d) => ({ ...d, options: { ...(d.options || {}), fill: f } }));
  const combine = draft?.options?.combine || null;
  const avgDivisor = draft?.options?.avgDivisor || null;
  const otherMetrics = metrics.filter((m) => m.id !== draft.metric);
  const setCombine = (patch) =>
    setDraft((d) => ({
      ...d,
      options: { ...(d.options || {}), combine: { ...(d.options?.combine || {}), ...patch } },
    }));
  const enableCombine = () => {
    setDraft((d) => {
      const options = { ...(d.options || {}) };
      delete options.avgDivisor;
      return { ...d, options: { ...options, combine: options.combine || { metric: otherMetrics[0]?.id, op: "divide" } } };
    });
  };
  const useSingle = () =>
    setDraft((d) => {
      const options = { ...(d.options || {}) };
      delete options.combine;
      delete options.avgDivisor;
      return { ...d, options };
    });
  const enableAvg = () =>
    setDraft((d) => {
      const options = { ...(d.options || {}) };
      delete options.combine;
      return { ...d, options: { ...options, avgDivisor: "businessDays" } };
    });
  let dataMode = "single";
  if (combine) dataMode = "combine";
  else if (avgDivisor) dataMode = "avg";
  const preview = (combine || avgDivisor) && api ? resolveWidgetData(api, draft) : null;
  const compareIds = (draft?.options?.metrics || []).filter((id) => id !== draft.metric);
  const toggleCompare = (id) =>
    setDraft((d) => {
      const cur = (d.options?.metrics || []).filter((x) => x !== d.metric);
      let next;
      if (cur.includes(id)) next = cur.filter((x) => x !== id);
      else if (cur.length < 3) next = [...cur, id];
      else next = cur;
      return { ...d, options: { ...(d.options || {}), metrics: next } };
    });

  const save = () => {
    onSave({
      title: draft.title || "",
      metric: draft.metric,
      chartType: draft.chartType,
      options: draft.options || {},
    });
    onClose();
  };

  return (
    <div
      className="db-modal-scrim"
      role="presentation"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="db-modal db-modal-edit" role="dialog" aria-modal="true" aria-label="Edit widget">
        <div className="db-modal-inner">
          <div className="db-modal-head">
            <div>
              <div className="db-modal-t">Edit widget</div>
              <div className="db-modal-s">Change what this card shows and how it looks.</div>
            </div>
            <button type="button" className="db-iconbtn" onClick={onClose} aria-label="Close">
              <IcClose />
            </button>
          </div>

          <div className="db-edit-grid">
            <div className="db-editcol">
              <div className="db-field">
                <span className="db-label">Data</span>
                <div className="db-seg" style={{ alignSelf: "flex-start" }}>
                  <button type="button" aria-pressed={dataMode === "single"} onClick={useSingle}>
                    One metric
                  </button>
                  <button type="button" aria-pressed={dataMode === "combine"} onClick={enableCombine}>
                    Combine two
                  </button>
                  <button type="button" aria-pressed={dataMode === "avg"} onClick={enableAvg}>
                    Average per day
                  </button>
                </div>
                {dataMode === "combine" && (
                  <div className="db-formula" style={{ marginTop: 8 }}>
                      <MetricSelect
                        value={draft.metric}
                        options={metrics}
                        ariaLabel="First metric"
                        onChange={(id) => set("metric", id)}
                      />
                      <span className="db-ops">
                        {COMBINE_OPS.map((o) => (
                          <button
                            key={o.id}
                            type="button"
                            className="db-op"
                            aria-pressed={(combine.op || "divide") === o.id}
                            aria-label={o.id}
                            onClick={() => setCombine({ op: o.id })}
                          >
                            {o.sym}
                          </button>
                        ))}
                      </span>
                      <MetricSelect
                        value={combine.metric}
                        options={otherMetrics}
                        ariaLabel="Second metric"
                        onChange={(id) => setCombine({ metric: id })}
                      />
                  </div>
                )}
                {dataMode === "avg" && (
                  <div style={{ marginTop: 8 }}>
                    <MetricSelect
                      value={draft.metric}
                      options={metrics}
                      ariaLabel="Metric"
                      onChange={(id) => set("metric", id)}
                    />
                    <div className="db-hint" style={{ marginTop: 6 }}>
                      Shown as the total ÷ business days (Mon–Fri) in whatever period the board is set to.
                    </div>
                  </div>
                )}
                {dataMode === "single" && (
                  <div style={{ marginTop: 8 }}>
                    <MetricSelect
                      value={draft.metric}
                      options={metrics}
                      ariaLabel="Metric"
                      onChange={(id) => set("metric", id)}
                    />
                  </div>
                )}
              </div>

              <div className="db-field">
                <span className="db-label">Title</span>
                <input
                  className="db-input"
                  value={draft.title || ""}
                  placeholder={
                    (preview && preview.metric.title) ||
                    (metrics.find((m) => m.id === draft.metric) || {}).title ||
                    "Widget title"
                  }
                  onChange={(e) => set("title", e.target.value)}
                />
              </div>

              <div className="db-field">
                <span className="db-label">Show as</span>
                <div className="db-types">
                  {CHART_TYPES.map((o) => (
                    <button
                      key={o.t}
                      type="button"
                      className="db-type"
                      aria-pressed={draft.chartType === o.t}
                      onClick={() => set("chartType", o.t)}
                    >
                      {o.ic}
                      <span>{o.label}</span>
                    </button>
                  ))}
                </div>
              </div>

              {draft.chartType === "compare" && (
                <div className="db-field">
                  <span className="db-label">Compare with (up to 3)</span>
                  <div className="db-chips">
                    {metrics
                      .filter((m) => m.id !== draft.metric)
                      .map((m) => (
                        <button
                          key={m.id}
                          type="button"
                          className="db-chip"
                          aria-pressed={compareIds.includes(m.id)}
                          onClick={() => toggleCompare(m.id)}
                        >
                          {m.title}
                        </button>
                      ))}
                  </div>
                </div>
              )}

              <div className="db-row">
                <div className="db-field">
                  <span className="db-label">Accent</span>
                  <div className="db-swatches">
                    {ACCENTS.map((a) => (
                      <button
                        key={a.id}
                        type="button"
                        className="db-swatch"
                        aria-pressed={accent === a.id}
                        aria-label={a.label}
                        title={a.label}
                        style={{ background: a.c || "var(--db-brand)" }}
                        onClick={() => setAccent(a.id)}
                      />
                    ))}
                  </div>
                </div>
                <div className="db-field">
                  <span className="db-label">Background</span>
                  <div className="db-seg" style={{ alignSelf: "flex-start" }}>
                    <button type="button" aria-pressed={fill === "white"} onClick={() => setFill("white")}>
                      White
                    </button>
                    <button type="button" aria-pressed={fill === "tint"} onClick={() => setFill("tint")}>
                      Soft tint
                    </button>
                  </div>
                </div>
              </div>

              <div className="db-row">
                {draft.chartType === "stat" && (
                  <div className="db-field">
                    <span className="db-label">Graph</span>
                    <div className="db-seg" style={{ alignSelf: "flex-start" }}>
                      {[
                        { id: "bottom", label: "Under" },
                        { id: "top", label: "Top" },
                        { id: "left", label: "Left" },
                        { id: "right", label: "Right" },
                        { id: "off", label: "Off" },
                      ].map((g) => (
                        <button key={g.id} type="button" aria-pressed={graph === g.id} onClick={() => setGraph(g.id)}>
                          {g.label}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
                <div className="db-field">
                  <span className="db-label">Group data by</span>
                  <div className="db-seg" style={{ alignSelf: "flex-start" }}>
                    <button type="button" aria-pressed={granularity === "day"} onClick={() => setGran("day")}>
                      Daily
                    </button>
                    <button type="button" aria-pressed={granularity === "week"} onClick={() => setGran("week")}>
                      Weekly
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <EditPreview draft={draft} api={api} />
          </div>

          <div className="db-modal-foot">
            <button type="button" className="db-btn ghost" onClick={onClose}>
              Cancel
            </button>
            <button type="button" className="db-btn primary" onClick={save}>
              Save changes
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

WidgetEditDrawer.propTypes = {
  open: PropTypes.bool,
  widget: PropTypes.object,
  metrics: PropTypes.array,
  api: PropTypes.object,
  onSave: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
};
